import { z } from 'zod';
/**
 * Compliance control library.
 *
 * The library separates four claims that are routinely (and dangerously)
 * conflated, and never lets one imply another:
 *   1. configured   — the control exists in this library with an owner
 *   2. evidenced    — current evidence has been collected and is unexpired
 *   3. tested       — the control's effectiveness was tested in its window
 *   4. interpreted  — the LEGAL reading is counsel-approved configuration
 * A green row is exactly those four flags; nothing here ever claims a
 * certification. Legal interpretation enters the system as counsel-approved
 * configuration text, never as invented code claims.
 */
export const COMPLIANCE_FRAMEWORKS = ['dpdp_act_2023', 'iso_27001', 'internal_policy'];
export const ComplianceFrameworkSchema = z.enum(COMPLIANCE_FRAMEWORKS);
export const CONTROL_TEST_FREQUENCIES = ['monthly', 'quarterly', 'semiannual', 'annual'];
export const ControlTestFrequencySchema = z.enum(CONTROL_TEST_FREQUENCIES);
export const EVIDENCE_STATUSES = ['requested', 'collected', 'expired', 'rejected'];
export const EvidenceStatusSchema = z.enum(EVIDENCE_STATUSES);
export const ComplianceControlSchema = z.object({
    id: z.uuid(),
    controlKey: z.string(),
    framework: ComplianceFrameworkSchema,
    title: z.string(),
    obligation: z.string(),
    /** Counsel-approved interpretation text; null until counsel has approved. */
    counselInterpretation: z.string().nullable(),
    counselApprovedAt: z.iso.datetime().nullable(),
    jurisdictions: z.array(z.string()),
    deploymentTypes: z.array(z.enum(['cloud', 'on_prem', 'hybrid'])),
    ownerUserId: z.uuid().nullable(),
    ownerDisplayName: z.string().nullable(),
    testFrequency: ControlTestFrequencySchema,
    lastTestedAt: z.iso.datetime().nullable(),
    lastTestOutcome: z.enum(['effective', 'ineffective']).nullable(),
    nextTestDueAt: z.iso.datetime().nullable(),
    /** The four independent status flags. */
    configured: z.boolean(),
    evidenced: z.boolean(),
    tested: z.boolean(),
    interpreted: z.boolean(),
    exceptionReason: z.string().nullable(),
    exceptionExpiresAt: z.iso.datetime().nullable(),
    remediationNote: z.string().nullable(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
});
export const ComplianceEvidenceSchema = z.object({
    id: z.uuid(),
    controlId: z.uuid(),
    title: z.string(),
    /** Reference/link into the evidence store — never clinical content. */
    reference: z.string(),
    status: EvidenceStatusSchema,
    collectedAt: z.iso.datetime().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    collectedByDisplayName: z.string().nullable(),
});
export const ComplianceEvidenceInputSchema = z
    .object({
    title: z.string().min(3).max(200),
    reference: z.string().min(3).max(500),
    expiresAt: z.iso.datetime().optional(),
})
    .strict();
export const ControlTestInputSchema = z
    .object({
    outcome: z.enum(['effective', 'ineffective']),
    note: z.string().min(10).max(2000),
    expectedVersion: z.number().int(),
})
    .strict();
export const ControlExceptionInputSchema = z
    .object({
    reason: z.string().min(10).max(1000),
    expiresAt: z.iso.datetime(),
    expectedVersion: z.number().int(),
})
    .strict();
export const ComplianceListResponseSchema = z.object({
    items: z.array(ComplianceControlSchema),
    total: z.number().int(),
});
//# sourceMappingURL=compliance.js.map